import { Injectable, signal, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { Observable } from 'rxjs';
import {
  Customer,
  CustomerCreateRequest,
  CustomerUpdateRequest,
  PaginatedCustomersResponse,
} from '../models/customer.model';
import { CustomerApiService } from './api/customer-api.service';

/**
 * Serviço responsável pelo gerenciamento de estado e lógica de negócio de clientes.
 * Delega chamadas HTTP ao CustomerApiService e mantém o estado local via signals.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  readonly customerApiService = inject(CustomerApiService);
  private readonly transloco = inject(TranslocoService);

  // ─── Signals de estado ────────────────────────────────────────────────────
  readonly _customers = signal<Customer[]>([]);
  readonly _isLoading = signal(false);
  readonly _totalCount = signal(0);

  /** Lista atual de clientes carregados */
  customers = this._customers.asReadonly();

  /** Indicador de operação em andamento */
  isLoading = this._isLoading.asReadonly();

  /** Total de clientes disponíveis (para paginação) */
  totalCount = this._totalCount.asReadonly();

  /**
   * Lista todos os clientes com paginação, atualizando o estado local.
   *
   * @param pageNumber Número da página (inicia em 1)
   * @param pageSize Quantidade de itens por página
   * @param searchTerm Termo de busca opcional para filtrar resultados
   * @returns Resposta paginada com a lista de clientes
   */
  async getCustomers(pageNumber = 1, pageSize = 10, searchTerm = ''): Promise<PaginatedCustomersResponse> {
    this._isLoading.set(true);
    try {
      const response = await this.customerApiService.getCustomers(pageNumber, pageSize, searchTerm);
      this._customers.set(response?.items ?? []);
      this._totalCount.set(response?.totalCount ?? 0);
      return response;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Busca todos os clientes sem paginação para uso em filtros e seletores.
   *
   * @returns Lista completa de clientes disponíveis
   */
  async getAllForFilter(): Promise<Customer[]> {
    try {
      return await this.customerApiService.getAllForFilter();
    } catch (error) {
      console.error('Erro ao buscar clientes para filtro:', error);
      return [];
    }
  }

  /**
   * Busca um cliente específico pelo identificador, atualizando o estado de loading.
   *
   * @param id Identificador único do cliente
   * @returns Dados do cliente encontrado ou null em caso de erro
   */
  async getCustomerById(id: string): Promise<Customer | null> {
    this._isLoading.set(true);
    try {
      return await this.customerApiService.getCustomerById(id);
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Cria um novo cliente, aplicando sanitização nos dados antes do envio.
   *
   * @param data Dados do cliente a ser criado
   * @returns Resultado da operação com status de sucesso e mensagem de erro opcional
   */
  async createCustomer(data: CustomerCreateRequest): Promise<{ success: boolean; error?: string }> {
    this._isLoading.set(true);
    try {
      const sanitizedData: CustomerCreateRequest = {
        nome: data.nome.trim(),
        email: data.email.trim(),
        cnpj: data.cnpj.trim(),
        telefone: data.telefone.trim(),
        referralCode: data.referralCode?.trim() || null,
      };

      await this.customerApiService.createCustomer(sanitizedData);
      return { success: true };
    } catch (error: unknown) {
      console.error('HTTP ERROR:', error);
      let errorMessage: string;
      if (error instanceof HttpErrorResponse) {
        errorMessage = this.customerApiService.getErrorMessage(error);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = this.transloco.translate('shared.apiErrors.createCustomer');
      }
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Atualiza os dados de um cliente existente.
   *
   * @param id Identificador único do cliente
   * @param data Dados atualizados do cliente
   * @returns Resultado da operação com status de sucesso e mensagem de erro opcional
   */
  async updateCustomer(id: string, data: CustomerUpdateRequest): Promise<{ success: boolean; error?: string }> {
    this._isLoading.set(true);
    try {
      await this.customerApiService.updateCustomer(id, data);
      return { success: true };
    } catch (error: unknown) {
      console.error('HTTP ERROR:', error);
      let message = this.transloco.translate('shared.apiErrors.updateCustomer');
      if (error instanceof HttpErrorResponse) {
        message = this.customerApiService.getErrorMessage(error);
      } else if (error instanceof Error) {
        message = error.message;
      }
      return { success: false, error: message };
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Atualiza o status de ativação de um cliente.
   *
   * @param id Identificador único do cliente
   * @param isActive Novo status de ativação
   */
  async updateCustomerStatus(id: string, isActive: boolean): Promise<void> {
    this._isLoading.set(true);
    try {
      await this.customerApiService.updateCustomerStatus(id, isActive);
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Remove um cliente da base de dados.
   *
   * @param id Identificador único do cliente
   * @returns true se a exclusão foi bem-sucedida
   */
  async deleteCustomer(id: string): Promise<boolean> {
    this._isLoading.set(true);
    try {
      await this.customerApiService.deleteCustomer(id);
      return true;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Busca clientes filtrando pelo termo informado, retornando um Observable para uso com RxJS.
   *
   * @param term Texto a ser usado como filtro de busca
   * @returns Observable com a resposta paginada de clientes filtrados
   */
  searchCustomers(term: string): Observable<PaginatedCustomersResponse> {
    return this.customerApiService.searchCustomers(term);
  }
}
